// li-funnel-redesign v2 — the sales page in the Duolingo style: chunky
// selectable plan rows, an uppercase CTA that sits on a 4px bottom edge, a
// free-vs-premium comparison and a guarantee block. The plan data flow is the
// baseline one (getPricesLoader -> SubscriptionForm), only the chrome changed.

import {
  DuoCheckIcon,
  DuoChevronIcon,
  DuoCrossIcon,
} from '@/components/v2/DuoIcons'
import DuoPlanRow from '@/components/v2/DuoPlanRow'
import SubscriptionForm from '@app-studio-web/ui/components/SubscriptionForm'
import SubscriptionTerms from '@app-studio-web/ui/components/SubscriptionTerms'
import type { GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import LoadingPage from '@app-studio-web/ui/pages/LoadingPage'
import type { Plan, StripePrice } from '@app-studio-web/ui/types'
import { Suspense, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'

interface PlanOption {
  plan: Plan
  stripePriceFull: StripePrice
  stripePriceIntro?: StripePrice
}

/** The plan line-up, in the baseline order, with the missing prices dropped. */
function getPlanOptions(pricesData: GetPricesData): PlanOption[] {
  const candidates: Array<{
    plan: Plan
    stripePriceFull: StripePrice | undefined
    stripePriceIntro: StripePrice | undefined
  }> = [
    {
      plan: 'top',
      stripePriceFull: pricesData.topPrice,
      stripePriceIntro: pricesData.topIntroPrice,
    },
    {
      plan: 'first',
      stripePriceFull: pricesData.firstPrice,
      stripePriceIntro: pricesData.firstIntroPrice,
    },
    {
      plan: 'one-time',
      stripePriceFull: pricesData.oneTimePrice,
      stripePriceIntro: undefined,
    },
    {
      plan: 'second',
      stripePriceFull: pricesData.secondPrice,
      stripePriceIntro: pricesData.secondIntroPrice,
    },
  ]

  return candidates.flatMap(({ plan, stripePriceFull, stripePriceIntro }) =>
    stripePriceFull ? [{ plan, stripePriceFull, stripePriceIntro }] : [],
  )
}

const COMPARISON_ROWS = [
  { freeIncluded: true, titleKey: 'pages.checkout.plans.bulletPoints.title1' },
  { freeIncluded: false, titleKey: 'pages.checkout.plans.bulletPoints.title2' },
  { freeIncluded: false, titleKey: 'pages.checkout.plans.bulletPoints.title3' },
  { freeIncluded: false, titleKey: 'pages.checkout.plans.bulletPoints.title4' },
]

const TESTIMONIALS = ['t1', 't2', 't3']

const FAQS = ['1', '2', '3']

function PlanPicker({
  currency,
  isSubmitting,
  planOptions,
  selectedPlan,
  setIsSubmitting,
  setSelectedPlan,
}: {
  currency: string
  isSubmitting: boolean
  planOptions: PlanOption[]
  selectedPlan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  setSelectedPlan: (plan: Plan) => void
}) {
  const { t } = useTranslation()

  const selectedOption =
    planOptions.find((option) => option.plan === selectedPlan) ?? planOptions[0]

  return (
    <div className="space-y-3">
      {planOptions.map((option, index) => (
        <DuoPlanRow
          bestOffer={index === 0}
          currency={currency}
          isSelected={option.plan === selectedPlan}
          key={option.plan}
          onSelect={() => setSelectedPlan(option.plan)}
          stripePriceFull={option.stripePriceFull}
          stripePriceIntro={option.stripePriceIntro}
        />
      ))}

      <div className="space-y-3 pt-3">
        <SubscriptionForm
          buttonClassName="duo-button-primary"
          buttonTitle={t('pages.checkout.plans.continue')}
          currency={currency}
          isSubmitting={isSubmitting}
          plan={selectedPlan}
          priceId={
            selectedOption?.stripePriceIntro?.id ??
            selectedOption?.stripePriceFull.id
          }
          setIsSubmitting={setIsSubmitting}
          stripePriceFull={selectedOption?.stripePriceFull}
        />

        <p className="text-duo-muted text-center text-sm font-bold">
          {t('pages.checkout.v2.plans.freeTrialNote')}
        </p>

        {selectedPlan !== 'one-time' && (
          <p className="text-duo-ink text-center text-sm font-bold">
            {t('pages.checkout.plans.cancelAnytime')}
          </p>
        )}

        <p className="text-duo-ink text-center text-sm font-bold">
          {t('pages.checkout.plans.moneyBackGuarantee')}
        </p>
      </div>

      <div className="text-duo-muted text-xs">
        <SubscriptionTerms
          currency={currency}
          plan={selectedPlan}
          stripePriceFull={selectedOption?.stripePriceFull}
          stripePriceIntro={selectedOption?.stripePriceIntro}
        />
      </div>
    </div>
  )
}

export default function PlansPage() {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('top')

  const { t } = useTranslation()

  const data = useLoaderData() as GetPricesData

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data}>
        {(pricesData: GetPricesData) => {
          const planOptions = getPlanOptions(pricesData)

          const picker = (
            <PlanPicker
              currency={pricesData.currency}
              isSubmitting={isSubmitting}
              planOptions={planOptions}
              selectedPlan={selectedPlan}
              setIsSubmitting={setIsSubmitting}
              setSelectedPlan={setSelectedPlan}
            />
          )

          return (
            <div className="min-h-dvh bg-white">
              <main className="duo-container space-y-14 py-10">
                <section className="space-y-4">
                  <p className="bg-duo-primary-soft text-duo-primary rounded-full px-4 py-2 text-center text-sm font-extrabold">
                    {t('pages.checkout.socialProofBanner')}
                  </p>

                  <h1 className="text-duo-ink text-center text-3xl font-extrabold text-balance">
                    <Trans
                      components={{
                        colored: <span className="text-duo-primary" />,
                      }}
                      i18nKey="pages.checkout.plans.heading"
                    />
                  </h1>

                  <p className="text-duo-muted text-center text-[15px] font-bold">
                    {t('pages.checkout.userCount')}
                  </p>
                </section>

                <section className="grid grid-cols-3 gap-3">
                  {[1, 2, 3].map((index) => (
                    <div
                      className="border-duo-line rounded-2xl border-2 border-b-4 px-2 py-4 text-center"
                      key={index}
                    >
                      <p className="text-duo-primary text-xl font-extrabold">
                        {t(`pages.checkout.successStats.stat${index}Value`)}
                      </p>

                      <p className="text-duo-muted mt-1 text-xs font-bold">
                        {t(`pages.checkout.successStats.stat${index}Label`)}
                      </p>
                    </div>
                  ))}
                </section>

                <section className="space-y-4">
                  <h2 className="duo-heading text-center">
                    {t('pages.checkout.v2.plans.chooseYourPlan')}
                  </h2>

                  <p className="text-duo-primary text-center text-sm font-extrabold">
                    {t('pages.checkout.viewingNow')}
                  </p>

                  {picker}
                </section>

                <section className="space-y-4">
                  <h2 className="duo-heading text-center">
                    {t('pages.checkout.v2.compare.title')}
                  </h2>

                  <div className="border-duo-line overflow-hidden rounded-2xl border-2 border-b-4">
                    <div className="border-duo-line text-duo-muted grid grid-cols-[1fr_72px_72px] items-center border-b-2 px-4 py-3 text-xs font-extrabold uppercase">
                      <span />
                      <span className="text-center">
                        {t('pages.checkout.v2.compare.freeLabel')}
                      </span>
                      <span className="text-duo-primary text-center">
                        {t('pages.checkout.v2.compare.premiumLabel')}
                      </span>
                    </div>

                    {COMPARISON_ROWS.map((row) => (
                      <div
                        className="border-duo-line grid grid-cols-[1fr_72px_72px] items-center border-b-2 px-4 py-3.5 last:border-b-0"
                        key={row.titleKey}
                      >
                        <span className="text-duo-ink pr-3 text-sm font-bold">
                          {t(row.titleKey)}
                        </span>

                        <span className="flex justify-center">
                          {row.freeIncluded ? (
                            <DuoCheckIcon className="text-duo-line-dark h-5 w-5" />
                          ) : (
                            <DuoCrossIcon className="h-5 w-5" />
                          )}
                        </span>

                        <span className="flex justify-center">
                          <DuoCheckIcon className="h-5 w-5" />
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="duo-heading text-center">
                    {t('pages.checkout.testimonials.heading')}
                  </h2>

                  {TESTIMONIALS.map((testimonial) => (
                    <figure
                      className="border-duo-line rounded-2xl border-2 border-b-4 px-4 py-4"
                      key={testimonial}
                    >
                      <p className="text-duo-yellow text-sm">★★★★★</p>

                      <blockquote className="text-duo-ink mt-2 text-[15px] leading-relaxed font-medium">
                        {t(`pages.checkout.testimonials.${testimonial}.text`)}
                      </blockquote>

                      <figcaption className="mt-3 text-sm font-extrabold">
                        {t(`pages.checkout.testimonials.${testimonial}.author`)}

                        <span className="text-duo-muted ml-2 font-bold">
                          {t(
                            `pages.checkout.testimonials.${testimonial}.detail`,
                          )}
                        </span>
                      </figcaption>
                    </figure>
                  ))}
                </section>

                <section className="border-duo-primary bg-duo-primary-soft rounded-2xl border-2 border-b-4 px-5 py-6 text-center">
                  <p className="bg-duo-primary mx-auto inline-block rounded-full px-3 py-1 text-xs font-extrabold text-white uppercase">
                    {t('pages.checkout.v2.guarantee.badge')}
                  </p>

                  <h2 className="text-duo-ink mt-3 text-xl font-extrabold">
                    {t(
                      'pages.checkout.plans.moneyBackGuaranteeComponent.title',
                    )}
                  </h2>

                  <p className="text-duo-ink mt-2 text-[15px] leading-relaxed">
                    {t(
                      'pages.checkout.plans.moneyBackGuaranteeComponent.description',
                    )}
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="duo-heading text-center">
                    {t('components.faq.heading')}
                  </h2>

                  {FAQS.map((faq) => (
                    <details
                      className="border-duo-line group rounded-2xl border-2 border-b-4 px-4 py-3.5"
                      key={faq}
                    >
                      <summary className="text-duo-ink flex cursor-pointer items-center justify-between gap-3 text-[15px] font-extrabold">
                        {t(`components.faq.title${faq}`)}

                        <DuoChevronIcon className="text-duo-line-dark shrink-0 transition-transform group-open:rotate-180" />
                      </summary>

                      <p className="text-duo-muted mt-3 text-sm leading-relaxed">
                        {t(`components.faq.text${faq}`, {
                          email:
                            import.meta.env.VITE_SUPPORT_EMAIL ??
                            'support@app-studio.ai',
                        })}
                      </p>
                    </details>
                  ))}
                </section>

                <section>{picker}</section>
              </main>
            </div>
          )
        }}
      </Await>
    </Suspense>
  )
}
